import { useState, useEffect, useCallback } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import {
  UsersIcon,
  RectangleStackIcon,
  ChartBarIcon,
  TvIcon,
  ShieldCheckIcon,
  UserPlusIcon,
  MagnifyingGlassIcon,
  XMarkIcon,
  CheckCircleIcon,
  ExclamationCircleIcon,
  ArrowPathIcon,
  DocumentTextIcon,
  ChevronDownIcon,
  ChevronRightIcon,
  ClipboardDocumentListIcon,
} from '@heroicons/react/24/outline';
import {
  UserGroupIcon,
  AcademicCapIcon,
} from '@heroicons/react/24/solid';
import { useAuth } from '@/contexts/AuthContext';
import Button from '@/components/Button';
import Input from '@/components/ui/Input';
import {
  getStats,
  getUsers,
  createUser,
  updateUser,
  getAllTemplates,
  getAllEvaluations,
  getLogs,
  runCreditMaintenance,
} from '@/services/adminApi';
import Modal from '@/components/ui/Modal';
import type { CreditMaintenanceResult } from '@/types/api';
import {
  listRegistrationRequests,
  approveRegistrationRequest,
  declineRegistrationRequest,
  blockRegistrationRequest,
} from '@/services/registrationRequestsApi';
import type { RegistrationRequest } from '@/services/registrationRequestsApi';
import type {
  PlatformStats,
  AdminUser,
  AdminTemplate,
  AdminEvaluation,
  CreateUserPayload,
  AppLog,
  LogListResponse,
  LogQueryParams,
} from '@/types/admin';
import { formatDate } from '@/utils/formatters';

// ─── Helpers ─────────────────────────────────────────────────────────────────

function truncateId(id: string | null | undefined): string {
  if (!id) return '—';
  return id.slice(0, 8) + '…';
}

function scoreColor(score: number | null): string {
  if (score === null) return 'var(--color-text-muted)';
  if (score >= 80) return 'var(--color-accent)';
  if (score >= 60) return 'var(--color-accent-gold)';
  return 'var(--color-accent-red)';
}

// ─── Role Badge ───────────────────────────────────────────────────────────────

function RoleBadge({ role }: { role: 'admin' | 'coach' | 'athlete' }) {
  const styles: Record<string, { color: string; bg: string; border: string }> = {
    admin: {
      color: 'var(--color-accent-gold)',
      bg: 'rgba(244, 201, 93, 0.12)',
      border: 'rgba(244, 201, 93, 0.30)',
    },
    coach: {
      color: 'var(--color-accent-blue)',
      bg: 'rgba(61, 169, 252, 0.12)',
      border: 'rgba(61, 169, 252, 0.30)',
    },
    athlete: {
      color: 'var(--color-accent)',
      bg: 'rgba(30, 215, 96, 0.12)',
      border: 'rgba(30, 215, 96, 0.30)',
    },
  };
  const s = styles[role];
  return (
    <span
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        fontSize: '11px',
        fontWeight: 600,
        padding: '2px 8px',
        borderRadius: '999px',
        backgroundColor: s.bg,
        color: s.color,
        border: `1px solid ${s.border}`,
        letterSpacing: '0.05em',
        textTransform: 'capitalize',
        whiteSpace: 'nowrap',
      }}
    >
      {role}
    </span>
  );
}

// ─── Status Badge ─────────────────────────────────────────────────────────────

function StatusBadge({ active }: { active: boolean }) {
  return (
    <span
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: '4px',
        fontSize: '11px',
        fontWeight: 600,
        padding: '2px 8px',
        borderRadius: '999px',
        backgroundColor: active ? 'rgba(30, 215, 96, 0.10)' : 'rgba(240, 93, 94, 0.10)',
        color: active ? 'var(--color-accent)' : 'var(--color-danger)',
        border: `1px solid ${active ? 'rgba(30, 215, 96, 0.25)' : 'rgba(240, 93, 94, 0.25)'}`,
        whiteSpace: 'nowrap',
      }}
    >
      <span
        style={{
          width: '5px',
          height: '5px',
          borderRadius: '999px',
          backgroundColor: active ? 'var(--color-accent)' : 'var(--color-danger)',
          flexShrink: 0,
        }}
      />
      {active ? 'Active' : 'Inactive'}
    </span>
  );
}

// ─── Loading Spinner ──────────────────────────────────────────────────────────

function Spinner() {
  return (
    <div
      style={{
        width: '20px',
        height: '20px',
        borderRadius: '999px',
        border: '2px solid var(--color-border-subtle)',
        borderTopColor: 'var(--color-accent)',
        animation: 'spin 0.7s linear infinite',
        flexShrink: 0,
      }}
    />
  );
}

// ─── Stat Card ────────────────────────────────────────────────────────────────

interface StatCardProps {
  label: string;
  value: number | null;
  icon: React.ReactNode;
  accentColor: string;
  glowColor: string;
}

function StatCard({ label, value, icon, accentColor, glowColor }: StatCardProps) {
  return (
    <div
      className="stat-card"
      style={{
        backgroundColor: 'var(--color-dark-card)',
        border: '1px solid var(--color-border-subtle)',
        borderRadius: '16px',
        padding: '24px',
        display: 'flex',
        flexDirection: 'column',
        gap: '16px',
        transition: 'border-color 200ms ease, box-shadow 200ms ease',
        cursor: 'default',
      }}
      onMouseEnter={(e) => {
        const el = e.currentTarget as HTMLDivElement;
        el.style.borderColor = accentColor + '44';
        el.style.boxShadow = `0 0 24px ${glowColor}`;
      }}
      onMouseLeave={(e) => {
        const el = e.currentTarget as HTMLDivElement;
        el.style.borderColor = 'var(--color-border-subtle)';
        el.style.boxShadow = 'none';
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <span
          style={{
            fontSize: '13px',
            fontWeight: 500,
            color: 'var(--color-text-muted)',
            fontFamily: 'Inter, sans-serif',
          }}
        >
          {label}
        </span>
        <div
          style={{
            width: '36px',
            height: '36px',
            borderRadius: '10px',
            backgroundColor: glowColor,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: accentColor,
            flexShrink: 0,
          }}
        >
          {icon}
        </div>
      </div>
      <div
        style={{
          fontFamily: 'var(--font-body)',
          fontWeight: 700,
          fontSize: '36px',
          color: 'var(--color-text-primary)',
          lineHeight: 1,
        }}
      >
        {value === null ? (
          <span style={{ fontSize: '24px', color: 'var(--color-text-muted)' }}>—</span>
        ) : (
          value.toLocaleString()
        )}
      </div>
    </div>
  );
}

// ─── Toast ────────────────────────────────────────────────────────────────────

interface ToastProps {
  type: 'success' | 'error';
  message: string;
  onClose: () => void;
}

function Toast({ type, message, onClose }: ToastProps) {
  useEffect(() => {
    const t = setTimeout(onClose, 4000);
    return () => clearTimeout(t);
  }, [onClose]);

  return (
    <div
      className="admin-toast"
      style={{
        position: 'fixed',
        bottom: '24px',
        right: '24px',
        zIndex: 100,
        display: 'flex',
        alignItems: 'center',
        gap: '10px',
        padding: '12px 16px',
        borderRadius: '12px',
        backgroundColor: 'var(--color-dark-elevated-1)',
        border: `1px solid ${type === 'success' ? 'rgba(30,215,96,0.30)' : 'rgba(240,93,94,0.30)'}`,
        boxShadow: 'var(--shadow-modal)',
        color: 'var(--color-text-primary)',
        fontSize: '13px',
        fontWeight: 500,
        maxWidth: '340px',
        animation: 'toast-in 0.25s cubic-bezier(0.22,1,0.36,1) both',
      }}
    >
      {type === 'success' ? (
        <CheckCircleIcon style={{ width: '18px', height: '18px', color: 'var(--color-accent)', flexShrink: 0 }} />
      ) : (
        <ExclamationCircleIcon style={{ width: '18px', height: '18px', color: 'var(--color-danger)', flexShrink: 0 }} />
      )}
      <span style={{ flex: 1 }}>{message}</span>
      <button
        onClick={onClose}
        style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--color-text-muted)', padding: '2px', display: 'flex' }}
      >
        <XMarkIcon style={{ width: '14px', height: '14px' }} />
      </button>
    </div>
  );
}

// ─── Add User Modal ───────────────────────────────────────────────────────────

interface AddUserModalProps {
  onClose: () => void;
  onCreated: (user: AdminUser) => void;
}

function AddUserModal({ onClose, onCreated }: AddUserModalProps) {
  const [form, setForm] = useState<CreateUserPayload>({
    email: '',
    password: '',
    display_name: '',
    role: 'coach',
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    setLoading(true);
    try {
      const payload: CreateUserPayload = {
        email: form.email.trim(),
        password: form.password,
        role: form.role,
      };
      if (form.display_name?.trim()) {
        payload.display_name = form.display_name.trim();
      }
      const created = await createUser(payload);
      onCreated(created);
    } catch (err: unknown) {
      const msg =
        (err as { response?: { data?: { detail?: string } } })?.response?.data?.detail ||
        'Failed to create user.';
      setError(msg);
    } finally {
      setLoading(false);
    }
  }

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 60,
        backgroundColor: 'var(--color-dark-overlay)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '24px',
        animation: 'admin-fade-in 0.15s ease both',
      }}
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div
        style={{
          width: '100%',
          maxWidth: '440px',
          backgroundColor: 'var(--color-dark-elevated-1)',
          border: '1px solid var(--color-border-subtle)',
          borderRadius: '20px',
          padding: '28px',
          boxShadow: 'var(--shadow-modal)',
          animation: 'admin-slide-up 0.22s cubic-bezier(0.22,1,0.36,1) both',
        }}
      >
        {/* Header */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '24px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <div
              style={{
                width: '36px', height: '36px', borderRadius: '10px',
                backgroundColor: 'rgba(61,169,252,0.12)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
              }}
            >
              <UserPlusIcon style={{ width: '18px', height: '18px', color: 'var(--color-accent-blue)' }} />
            </div>
            <h2 style={{ fontFamily: 'var(--font-body)', fontWeight: 700, fontSize: '16px', color: 'var(--color-text-primary)', margin: 0 }}>
              Add New User
            </h2>
          </div>
          <button
            onClick={onClose}
            style={{
              background: 'none', border: 'none', cursor: 'pointer',
              color: 'var(--color-text-muted)', padding: '4px',
              borderRadius: '6px', display: 'flex',
              transition: 'color 120ms ease',
            }}
            onMouseEnter={(e) => { (e.currentTarget as HTMLButtonElement).style.color = 'var(--color-text-primary)'; }}
            onMouseLeave={(e) => { (e.currentTarget as HTMLButtonElement).style.color = 'var(--color-text-muted)'; }}
          >
            <XMarkIcon style={{ width: '18px', height: '18px' }} />
          </button>
        </div>

        <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
          <Input
            label="Email"
            type="email"
            required
            value={form.email}
            onChange={(e) => setForm((p) => ({ ...p, email: e.target.value }))}
            placeholder="user@example.com"
          />
          <Input
            label="Password"
            type="password"
            required
            value={form.password}
            onChange={(e) => setForm((p) => ({ ...p, password: e.target.value }))}
            placeholder="Min. 8 characters"
          />
          <Input
            label="Display Name (optional)"
            type="text"
            value={form.display_name ?? ''}
            onChange={(e) => setForm((p) => ({ ...p, display_name: e.target.value }))}
            placeholder="Full name"
          />

          {/* Role selector */}
          <div>
            <label style={{ display: 'block', fontSize: '13px', fontWeight: 500, color: 'var(--color-text-secondary)', marginBottom: '6px' }}>
              Role
            </label>
            <div style={{ display: 'flex', gap: '8px' }}>
              {(['coach', 'athlete', 'admin'] as const).map((r) => (
                <button
                  key={r}
                  type="button"
                  onClick={() => setForm((p) => ({ ...p, role: r }))}
                  style={{
                    flex: 1,
                    padding: '8px 4px',
                    borderRadius: '10px',
                    border: `1px solid ${form.role === r ? getRoleAccent(r) + '66' : 'var(--color-border-subtle)'}`,
                    backgroundColor: form.role === r ? getRoleGlow(r) : 'var(--color-dark-elevated-2)',
                    color: form.role === r ? getRoleAccent(r) : 'var(--color-text-muted)',
                    fontSize: '12px',
                    fontWeight: 600,
                    textTransform: 'capitalize',
                    cursor: 'pointer',
                    transition: 'all 150ms ease',
                    textAlign: 'center',
                  }}
                >
                  {r}
                </button>
              ))}
            </div>
          </div>

          {error && (
            <p style={{ margin: 0, fontSize: '13px', color: 'var(--color-danger)', display: 'flex', alignItems: 'center', gap: '6px' }}>
              <ExclamationCircleIcon style={{ width: '14px', height: '14px', flexShrink: 0 }} />
              {error}
            </p>
          )}

          <div style={{ display: 'flex', gap: '10px', marginTop: '4px' }}>
            <Button type="button" variant="outline" onClick={onClose} style={{ flex: 1 }}>
              Cancel
            </Button>
            <Button type="submit" disabled={loading} style={{ flex: 1 }}>
              {loading ? (
                <span style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <Spinner /> Creating…
                </span>
              ) : (
                'Create User'
              )}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}

function getRoleAccent(role: string): string {
  if (role === 'admin') return 'var(--color-accent-gold)';
  if (role === 'coach') return 'var(--color-accent-blue)';
  return 'var(--color-accent)';
}

function getRoleGlow(role: string): string {
  if (role === 'admin') return 'rgba(244,201,93,0.12)';
  if (role === 'coach') return 'rgba(61,169,252,0.12)';
  return 'rgba(30,215,96,0.12)';
}

// ─── Tab types ────────────────────────────────────────────────────────────────

type TabId = 'overview' | 'users' | 'templates' | 'evaluations' | 'logs' | 'registration_requests';

const TABS: { id: TabId; label: string; icon: React.ComponentType<React.SVGProps<SVGSVGElement>> }[] = [
  { id: 'overview', label: 'Overview', icon: ChartBarIcon },
  { id: 'users', label: 'Users', icon: UsersIcon },
  { id: 'templates', label: 'Templates', icon: RectangleStackIcon },
  { id: 'evaluations', label: 'Evaluations', icon: ChartBarIcon },
  { id: 'logs', label: 'Logs', icon: DocumentTextIcon },
  { id: 'registration_requests', label: 'Requests', icon: ClipboardDocumentListIcon },
];

// ─── Credit maintenance card (S18 admin on-demand trigger) ─────────────────────

function CreditMaintenanceCard() {
  const [running, setRunning] = useState(false);
  const [result, setResult] = useState<CreditMaintenanceResult | null>(null);
  const [error, setError] = useState<string | null>(null);

  const handleRun = async () => {
    setRunning(true);
    setError(null);
    try {
      const res = await runCreditMaintenance();
      setResult(res);
    } catch (err: unknown) {
      const msg =
        (err as { response?: { data?: { detail?: string } } })?.response?.data?.detail ??
        'Credit maintenance failed. Check server logs.';
      setError(typeof msg === 'string' ? msg : JSON.stringify(msg));
    } finally {
      setRunning(false);
    }
  };

  const summaryRows: { label: string; value: number }[] = result
    ? [
        { label: 'Channels granted', value: result.granted_channels },
        { label: 'Total credits granted', value: result.total_credits },
        { label: 'Allocations expired', value: result.expired_allocations },
        { label: 'Reserves reaped', value: result.reaped_reserves },
        { label: 'Discrepancies', value: result.discrepancy_count },
      ]
    : [];

  return (
    <div
      style={{
        backgroundColor: 'var(--color-dark-card)',
        border: '1px solid var(--color-border-subtle)',
        borderRadius: '16px',
        padding: '20px 24px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: '16px',
        flexWrap: 'wrap',
      }}
    >
      <div>
        <h3
          style={{
            fontFamily: 'var(--font-body)',
            fontWeight: 700,
            fontSize: '15px',
            color: 'var(--color-text-primary)',
            margin: 0,
          }}
        >
          Credit maintenance
        </h3>
        <p style={{ margin: '4px 0 0', fontSize: '13px', color: 'var(--color-text-muted)' }}>
          Run the ledger cycle now: monthly grant, expire allocations, reap stale reserves, reconcile.
        </p>
      </div>
      <Button onClick={handleRun} disabled={running} size="sm" style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
        {running ? (
          <>
            <Spinner /> Running…
          </>
        ) : (
          'Run maintenance'
        )}
      </Button>

      {error && (
        <Modal isOpen onClose={() => setError(null)} title="Credit maintenance failed" size="sm">
          <p style={{ fontSize: '13px', color: 'var(--color-danger)', margin: 0 }}>{error}</p>
        </Modal>
      )}

      {result && (
        <Modal isOpen onClose={() => setResult(null)} title="Credit maintenance complete" size="sm">
          <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
            {!result.ledger_enabled && (
              <div
                style={{
                  padding: '10px 12px',
                  borderRadius: '8px',
                  backgroundColor: 'rgba(244,201,93,0.10)',
                  border: '1px solid rgba(244,201,93,0.25)',
                  color: 'var(--color-accent-gold)',
                  fontSize: '13px',
                }}
              >
                Credit ledger is disabled — no maintenance was performed (all counts 0).
              </div>
            )}
            {summaryRows.map((row) => (
              <div
                key={row.label}
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  padding: '7px 0',
                  borderBottom: '1px solid var(--color-border-subtle)',
                  fontSize: '13px',
                }}
              >
                <span style={{ color: 'var(--color-text-muted)' }}>{row.label}</span>
                <span style={{ color: 'var(--color-text-primary)', fontWeight: 600, fontVariantNumeric: 'tabular-nums' }}>
                  {row.value.toLocaleString()}
                </span>
              </div>
            ))}
            <p style={{ margin: '4px 0 0', fontSize: '12px', color: 'var(--color-text-muted)' }}>
              Ran at {formatDate(result.ran_at, 'MMM d, yyyy HH:mm')}
            </p>
          </div>
        </Modal>
      )}
    </div>
  );
}

// ─── Overview Tab ─────────────────────────────────────────────────────────────

function OverviewTab({ stats, loading }: { stats: PlatformStats | null; loading: boolean }) {
  const cards: StatCardProps[] = [
    {
      label: 'Total Users',
      value: stats?.total_users ?? null,
      icon: <UserGroupIcon style={{ width: '18px', height: '18px' }} />,
      accentColor: 'var(--color-accent-blue)',
      glowColor: 'rgba(61,169,252,0.12)',
    },
    {
      label: 'Coaches',
      value: stats?.coaches ?? null,
      icon: <AcademicCapIcon style={{ width: '18px', height: '18px' }} />,
      accentColor: 'var(--color-accent-blue)',
      glowColor: 'rgba(61,169,252,0.12)',
    },
    {
      label: 'Athletes',
      value: stats?.athletes ?? null,
      icon: <UsersIcon style={{ width: '18px', height: '18px' }} />,
      accentColor: 'var(--color-accent)',
      glowColor: 'rgba(30,215,96,0.12)',
    },
    {
      label: 'Templates',
      value: stats?.total_templates ?? null,
      icon: <RectangleStackIcon style={{ width: '18px', height: '18px' }} />,
      accentColor: 'var(--color-accent-orange)',
      glowColor: 'rgba(255,138,61,0.12)',
    },
    {
      label: 'Evaluations',
      value: stats?.total_evaluations ?? null,
      icon: <ChartBarIcon style={{ width: '18px', height: '18px' }} />,
      accentColor: 'var(--color-accent-gold)',
      glowColor: 'rgba(244,201,93,0.12)',
    },
    {
      label: 'Channels',
      value: stats?.total_channels ?? null,
      icon: <TvIcon style={{ width: '18px', height: '18px' }} />,
      accentColor: 'var(--color-accent-red)',
      glowColor: 'rgba(255,93,93,0.12)',
    },
  ];

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', padding: '64px 0' }}>
        <Spinner />
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))',
          gap: '16px',
        }}
      >
        {cards.map((card) => (
          <StatCard key={card.label} {...card} />
        ))}
      </div>
      <CreditMaintenanceCard />
    </div>
  );
}

// ─── Users Tab ────────────────────────────────────────────────────────────────

type RoleFilter = 'all' | 'coach' | 'athlete' | 'admin';

interface UsersTabProps {
  currentUserId: string;
  onToast: (type: 'success' | 'error', message: string) => void;
}

function UsersTab({ currentUserId, onToast }: UsersTabProps) {
  const [users, setUsers] = useState<AdminUser[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const pageSize = 20;
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [roleFilter, setRoleFilter] = useState<RoleFilter>('all');
  const [showAddModal, setShowAddModal] = useState(false);
  const [updatingId, setUpdatingId] = useState<string | null>(null);

  const fetchUsers = useCallback(async () => {
    setLoading(true);
    try {
      const params: Record<string, string | number> = { page, page_size: pageSize };
      if (search.trim()) params.search = search.trim();
      if (roleFilter !== 'all') params.role = roleFilter;
      const res = await getUsers(params);
      setUsers(res.users);
      setTotal(res.total);
    } catch {
      onToast('error', 'Failed to load users.');
    } finally {
      setLoading(false);
    }
  }, [page, search, roleFilter, onToast]);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  // Debounce search reset to page 1
  useEffect(() => {
    setPage(1);
  }, [search, roleFilter]);

  async function handleRoleChange(userId: string, newRole: 'admin' | 'coach' | 'athlete') {
    setUpdatingId(userId);
    try {
      const updated = await updateUser(userId, { role: newRole });
      setUsers((prev) => prev.map((u) => (u.id === userId ? updated : u)));
      onToast('success', 'Role updated successfully.');
    } catch {
      onToast('error', 'Failed to update role.');
    } finally {
      setUpdatingId(null);
    }
  }

  async function handleToggleActive(user: AdminUser) {
    setUpdatingId(user.id);
    try {
      const updated = await updateUser(user.id, { is_active: !user.is_active });
      setUsers((prev) => prev.map((u) => (u.id === user.id ? updated : u)));
      onToast('success', `User ${updated.is_active ? 'activated' : 'deactivated'}.`);
    } catch {
      onToast('error', 'Failed to update user status.');
    } finally {
      setUpdatingId(null);
    }
  }

  function handleUserCreated(newUser: AdminUser) {
    setShowAddModal(false);
    setUsers((prev) => [newUser, ...prev]);
    setTotal((t) => t + 1);
    onToast('success', `User ${newUser.email} created.`);
  }

  const totalPages = Math.ceil(total / pageSize);
  const roleFilters: { id: RoleFilter; label: string }[] = [
    { id: 'all', label: 'All' },
    { id: 'coach', label: 'Coaches' },
    { id: 'athlete', label: 'Athletes' },
    { id: 'admin', label: 'Admins' },
  ];

  return (
    <>
      {showAddModal && (
        <AddUserModal onClose={() => setShowAddModal(false)} onCreated={handleUserCreated} />
      )}

      {/* Toolbar */}
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '12px', alignItems: 'center', marginBottom: '20px' }}>
        {/* Search */}
        <div style={{ position: 'relative', flex: '1', minWidth: '200px', maxWidth: '360px' }}>
          <MagnifyingGlassIcon
            style={{
              position: 'absolute', left: '10px', top: '50%', transform: 'translateY(-50%)',
              width: '16px', height: '16px', color: 'var(--color-text-muted)', pointerEvents: 'none',
            }}
          />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search by name or email…"
            style={{
              width: '100%',
              height: '40px',
              borderRadius: '10px',
              border: '1px solid var(--color-border-subtle)',
              backgroundColor: 'var(--color-dark-elevated-2)',
              color: 'var(--color-text-primary)',
              fontSize: '13px',
              padding: '0 12px 0 34px',
              outline: 'none',
              transition: 'border-color 150ms ease',
              fontFamily: 'Inter, sans-serif',
            }}
            onFocus={(e) => { e.currentTarget.style.borderColor = 'var(--color-accent)'; }}
            onBlur={(e) => { e.currentTarget.style.borderColor = 'var(--color-border-subtle)'; }}
          />
        </div>

        {/* Role pills */}
        <div style={{ display: 'flex', gap: '6px' }}>
          {roleFilters.map((f) => (
            <button
              key={f.id}
              type="button"
              onClick={() => setRoleFilter(f.id)}
              style={{
                height: '36px',
                padding: '0 14px',
                borderRadius: '999px',
                border: `1px solid ${roleFilter === f.id ? 'var(--color-accent)' : 'var(--color-border-subtle)'}`,
                backgroundColor: roleFilter === f.id ? 'rgba(30,215,96,0.10)' : 'transparent',
                color: roleFilter === f.id ? 'var(--color-accent)' : 'var(--color-text-secondary)',
                fontSize: '13px',
                fontWeight: 500,
                cursor: 'pointer',
                transition: 'all 150ms ease',
              }}
            >
              {f.label}
            </button>
          ))}
        </div>

        <div style={{ marginLeft: 'auto' }}>
          <Button onClick={() => setShowAddModal(true)} size="sm" style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
            <UserPlusIcon style={{ width: '15px', height: '15px' }} />
            Add User
          </Button>
        </div>
      </div>

      {/* Table */}
      <div
        style={{
          backgroundColor: 'var(--color-dark-card)',
          border: '1px solid var(--color-border-subtle)',
          borderRadius: '16px',
          overflow: 'hidden',
        }}
      >
        {loading ? (
          <div style={{ display: 'flex', justifyContent: 'center', padding: '48px' }}>
            <Spinner />
          </div>
        ) : users.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '48px', color: 'var(--color-text-muted)', fontSize: '14px' }}>
            No users found.
          </div>
        ) : (
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
            <thead>
              <tr style={{ borderBottom: '1px solid var(--color-border-subtle)' }}>
                {['Name', 'Email', 'Role', 'Status', 'Joined', 'Actions'].map((col) => (
                  <th
                    key={col}
                    style={{
                      padding: '12px 16px',
                      textAlign: 'left',
                      fontSize: '11px',
                      fontWeight: 600,
                      color: 'var(--color-text-muted)',
                      letterSpacing: '0.06em',
                      textTransform: 'uppercase',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {col}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {users.map((user, idx) => {
                const isSelf = user.id === currentUserId;
                const isUpdating = updatingId === user.id;
                return (
                  <tr
                    key={user.id}
                    style={{
                      borderBottom: idx < users.length - 1 ? '1px solid var(--color-border-subtle)' : 'none',
                      backgroundColor: idx % 2 === 0 ? 'transparent' : 'rgba(255,255,255,0.015)',
                      opacity: isUpdating ? 0.6 : 1,
                      transition: 'background-color 120ms ease, opacity 150ms ease',
                    }}
                    onMouseEnter={(e) => {
                      (e.currentTarget as HTMLTableRowElement).style.backgroundColor = 'rgba(255,255,255,0.03)';
                    }}
                    onMouseLeave={(e) => {
                      (e.currentTarget as HTMLTableRowElement).style.backgroundColor =
                        idx % 2 === 0 ? 'transparent' : 'rgba(255,255,255,0.015)';
                    }}
                  >
                    {/* Name */}
                    <td style={{ padding: '12px 16px' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                        <div
                          style={{
                            width: '30px', height: '30px', borderRadius: '999px', flexShrink: 0,
                            backgroundColor: getRoleGlow(user.role),
                            border: `1px solid ${getRoleAccent(user.role)}33`,
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                          }}
                        >
                          <span style={{ fontSize: '12px', fontWeight: 700, color: getRoleAccent(user.role), fontFamily: 'var(--font-body)' }}>
                            {(user.display_name || user.email).charAt(0).toUpperCase()}
                          </span>
                        </div>
                        <span style={{ fontWeight: 500, color: 'var(--color-text-primary)' }}>
                          {user.display_name || '—'}
                          {isSelf && (
                            <span style={{ marginLeft: '6px', fontSize: '10px', color: 'var(--color-text-muted)' }}>(you)</span>
                          )}
                        </span>
                      </div>
                    </td>

                    {/* Email */}
                    <td style={{ padding: '12px 16px', color: 'var(--color-text-secondary)', fontFamily: 'Inter, monospace' }}>
                      {user.email}
                    </td>

                    {/* Role */}
                    <td style={{ padding: '12px 16px' }}>
                      {isSelf ? (
                        <RoleBadge role={user.role} />
                      ) : (
                        <select
                          value={user.role}
                          disabled={isUpdating}
                          onChange={(e) =>
                            handleRoleChange(user.id, e.target.value as 'admin' | 'coach' | 'athlete')
                          }
                          style={{
                            height: '30px',
                            padding: '0 8px',
                            borderRadius: '8px',
                            border: '1px solid var(--color-border-subtle)',
                            backgroundColor: 'var(--color-dark-elevated-2)',
                            color: 'var(--color-text-primary)',
                            fontSize: '12px',
                            fontWeight: 500,
                            cursor: 'pointer',
                            outline: 'none',
                            fontFamily: 'Inter, sans-serif',
                          }}
                        >
                          <option value="coach">Coach</option>
                          <option value="athlete">Athlete</option>
                          <option value="admin">Admin</option>
                        </select>
                      )}
                    </td>

                    {/* Status */}
                    <td style={{ padding: '12px 16px' }}>
                      <StatusBadge active={user.is_active} />
                    </td>

                    {/* Joined */}
                    <td style={{ padding: '12px 16px', color: 'var(--color-text-muted)', whiteSpace: 'nowrap' }}>
                      {formatDate(user.created_at)}
                    </td>

                    {/* Actions */}
                    <td style={{ padding: '12px 16px' }}>
                      {isSelf ? (
                        <span style={{ fontSize: '11px', color: 'var(--color-text-muted)' }}>—</span>
                      ) : (
                        <button
                          onClick={() => handleToggleActive(user)}
                          disabled={isUpdating}
                          style={{
                            height: '28px',
                            padding: '0 12px',
                            borderRadius: '999px',
                            border: `1px solid ${user.is_active ? 'rgba(240,93,94,0.30)' : 'rgba(30,215,96,0.30)'}`,
                            backgroundColor: user.is_active ? 'rgba(240,93,94,0.08)' : 'rgba(30,215,96,0.08)',
                            color: user.is_active ? 'var(--color-danger)' : 'var(--color-accent)',
                            fontSize: '11px',
                            fontWeight: 600,
                            cursor: 'pointer',
                            transition: 'all 150ms ease',
                          }}
                        >
                          {user.is_active ? 'Deactivate' : 'Activate'}
                        </button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '16px' }}>
          <span style={{ fontSize: '12px', color: 'var(--color-text-muted)' }}>
            {total} users · page {page} of {totalPages}
          </span>
          <div style={{ display: 'flex', gap: '8px' }}>
            <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage((p) => p - 1)}>
              Previous
            </Button>
            <Button variant="outline" size="sm" disabled={page >= totalPages} onClick={() => setPage((p) => p + 1)}>
              Next
            </Button>
          </div>
        </div>
      )}
    </>
  );
}

// ─── Templates Tab ────────────────────────────────────────────────────────────

function TemplatesTab({ onToast }: { onToast: (type: 'success' | 'error', message: string) => void }) {
  const [templates, setTemplates] = useState<AdminTemplate[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const pageSize = 20;
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    getAllTemplates({ page, page_size: pageSize })
      .then((res) => {
        if (cancelled) return;
        setTemplates(res.templates);
        setTotal(res.total);
      })
      .catch(() => onToast('error', 'Failed to load templates.'))
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [page, onToast]);

  const totalPages = Math.ceil(total / pageSize);

  return (
    <>
      <div
        style={{
          backgroundColor: 'var(--color-dark-card)',
          border: '1px solid var(--color-border-subtle)',
          borderRadius: '16px',
          overflow: 'hidden',
        }}
      >
        {loading ? (
          <div style={{ display: 'flex', justifyContent: 'center', padding: '48px' }}>
            <Spinner />
          </div>
        ) : (templates?.length ?? 0) === 0 ? (
          <div style={{ textAlign: 'center', padding: '48px', color: 'var(--color-text-muted)', fontSize: '14px' }}>
            No templates found.
          </div>
        ) : (
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
            <thead>
              <tr style={{ borderBottom: '1px solid var(--color-border-subtle)' }}>
                {['Name', 'Owner', 'Channel', 'Sport', 'Type', 'Created'].map((col) => (
                  <th
                    key={col}
                    style={{
                      padding: '12px 16px',
                      textAlign: 'left',
                      fontSize: '11px',
                      fontWeight: 600,
                      color: 'var(--color-text-muted)',
                      letterSpacing: '0.06em',
                      textTransform: 'uppercase',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {col}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {(templates ?? []).map((tmpl, idx) => (
                <tr
                  key={tmpl.id}
                  style={{
                    borderBottom: idx < templates.length - 1 ? '1px solid var(--color-border-subtle)' : 'none',
                    backgroundColor: idx % 2 === 0 ? 'transparent' : 'rgba(255,255,255,0.015)',
                    transition: 'background-color 120ms ease',
                  }}
                  onMouseEnter={(e) => { (e.currentTarget as HTMLTableRowElement).style.backgroundColor = 'rgba(255,255,255,0.03)'; }}
                  onMouseLeave={(e) => { (e.currentTarget as HTMLTableRowElement).style.backgroundColor = idx % 2 === 0 ? 'transparent' : 'rgba(255,255,255,0.015)'; }}
                >
                  <td style={{ padding: '12px 16px' }}>
                    <Link
                      to={`/templates/${tmpl.id}`}
                      style={{
                        color: 'var(--color-accent-blue)',
                        textDecoration: 'none',
                        fontWeight: 500,
                        transition: 'color 120ms ease',
                      }}
                      onMouseEnter={(e) => { e.currentTarget.style.textDecoration = 'underline'; }}
                      onMouseLeave={(e) => { e.currentTarget.style.textDecoration = 'none'; }}
                    >
                      {tmpl.name}
                    </Link>
                  </td>
                  <td style={{ padding: '12px 16px', color: 'var(--color-text-secondary)' }}>{tmpl.owner_email}</td>
                  <td style={{ padding: '12px 16px', color: 'var(--color-text-muted)' }}>{tmpl.channel_name || '—'}</td>
                  <td style={{ padding: '12px 16px', color: 'var(--color-text-secondary)' }}>{tmpl.sport_category || '—'}</td>
                  <td style={{ padding: '12px 16px', color: 'var(--color-text-secondary)' }}>{tmpl.movement_type || '—'}</td>
                  <td style={{ padding: '12px 16px', color: 'var(--color-text-muted)', whiteSpace: 'nowrap' }}>
                    {formatDate(tmpl.created_at)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {totalPages > 1 && (
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '16px' }}>
          <span style={{ fontSize: '12px', color: 'var(--color-text-muted)' }}>
            {total} templates · page {page} of {totalPages}
          </span>
          <div style={{ display: 'flex', gap: '8px' }}>
            <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage((p) => p - 1)}>
              Previous
            </Button>
            <Button variant="outline" size="sm" disabled={page >= totalPages} onClick={() => setPage((p) => p + 1)}>
              Next
            </Button>
          </div>
        </div>
      )}
    </>
  );
}

// ─── Evaluations Tab ──────────────────────────────────────────────────────────

function EvaluationsTab({ onToast }: { onToast: (type: 'success' | 'error', message: string) => void }) {
  const [evaluations, setEvaluations] = useState<AdminEvaluation[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const pageSize = 20;
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    getAllEvaluations({ page, page_size: pageSize })
      .then((res) => {
        if (cancelled) return;
        setEvaluations(res.evaluations);
        setTotal(res.total);
      })
      .catch(() => onToast('error', 'Failed to load evaluations.'))
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [page, onToast]);

  const totalPages = Math.ceil(total / pageSize);

  return (
    <>
      <div
        style={{
          backgroundColor: 'var(--color-dark-card)',
          border: '1px solid var(--color-border-subtle)',
          borderRadius: '16px',
          overflow: 'hidden',
        }}
      >
        {loading ? (
          <div style={{ display: 'flex', justifyContent: 'center', padding: '48px' }}>
            <Spinner />
          </div>
        ) : evaluations.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '48px', color: 'var(--color-text-muted)', fontSize: '14px' }}>
            No evaluations found.
          </div>
        ) : (
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
            <thead>
              <tr style={{ borderBottom: '1px solid var(--color-border-subtle)' }}>
                {['ID', 'User', 'Template', 'Score', 'Date'].map((col) => (
                  <th
                    key={col}
                    style={{
                      padding: '12px 16px',
                      textAlign: 'left',
                      fontSize: '11px',
                      fontWeight: 600,
                      color: 'var(--color-text-muted)',
                      letterSpacing: '0.06em',
                      textTransform: 'uppercase',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {col}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {evaluations.map((ev, idx) => (
                <tr
                  key={ev.id}
                  style={{
                    borderBottom: idx < evaluations.length - 1 ? '1px solid var(--color-border-subtle)' : 'none',
                    backgroundColor: idx % 2 === 0 ? 'transparent' : 'rgba(255,255,255,0.015)',
                    transition: 'background-color 120ms ease',
                  }}
                  onMouseEnter={(e) => { (e.currentTarget as HTMLTableRowElement).style.backgroundColor = 'rgba(255,255,255,0.03)'; }}
                  onMouseLeave={(e) => { (e.currentTarget as HTMLTableRowElement).style.backgroundColor = idx % 2 === 0 ? 'transparent' : 'rgba(255,255,255,0.015)'; }}
                >
                  <td style={{ padding: '12px 16px' }}>
                    <Link
                      to={`/evaluations/${ev.id}`}
                      style={{
                        fontFamily: 'monospace',
                        fontSize: '12px',
                        color: 'var(--color-accent-blue)',
                        textDecoration: 'none',
                        transition: 'color 120ms ease',
                      }}
                      onMouseEnter={(e) => { e.currentTarget.style.textDecoration = 'underline'; }}
                      onMouseLeave={(e) => { e.currentTarget.style.textDecoration = 'none'; }}
                    >
                      {truncateId(ev.id)}
                    </Link>
                  </td>
                  <td style={{ padding: '12px 16px', color: 'var(--color-text-secondary)' }}>{ev.user_email}</td>
                  <td style={{ padding: '12px 16px', color: 'var(--color-text-secondary)' }}>
                    {ev.template_name || truncateId(ev.template_id)}
                  </td>
                  <td style={{ padding: '12px 16px' }}>
                    {ev.overall_score !== null ? (
                      <span
                        style={{
                          fontFamily: 'var(--font-body)',
                          fontWeight: 700,
                          fontSize: '14px',
                          color: scoreColor(ev.overall_score),
                        }}
                      >
                        {Math.round(ev.overall_score)}
                      </span>
                    ) : (
                      <span style={{ color: 'var(--color-text-muted)' }}>—</span>
                    )}
                  </td>
                  <td style={{ padding: '12px 16px', color: 'var(--color-text-muted)', whiteSpace: 'nowrap' }}>
                    {formatDate(ev.created_at)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {totalPages > 1 && (
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '16px' }}>
          <span style={{ fontSize: '12px', color: 'var(--color-text-muted)' }}>
            {total} evaluations · page {page} of {totalPages}
          </span>
          <div style={{ display: 'flex', gap: '8px' }}>
            <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage((p) => p - 1)}>
              Previous
            </Button>
            <Button variant="outline" size="sm" disabled={page >= totalPages} onClick={() => setPage((p) => p + 1)}>
              Next
            </Button>
          </div>
        </div>
      )}
    </>
  );
}

// ─── Logs Tab ─────────────────────────────────────────────────────────────────

type LogLevel = 'ALL' | 'ERROR' | 'WARNING' | 'INFO';
type LogEventType = 'all' | 'error' | 'request' | 'auth' | 'evaluation';
type TimeRange = '1h' | '6h' | '24h' | '7d';

function timeRangeToSince(range: TimeRange): string {
  const now = new Date();
  const offsets: Record<TimeRange, number> = {
    '1h': 1 * 60 * 60 * 1000,
    '6h': 6 * 60 * 60 * 1000,
    '24h': 24 * 60 * 60 * 1000,
    '7d': 7 * 24 * 60 * 60 * 1000,
  };
  return new Date(now.getTime() - offsets[range]).toISOString();
}

function relativeTime(iso: string): string {
  const diff = Date.now() - new Date(iso).getTime();
  const s = Math.floor(diff / 1000);
  if (s < 60) return `${s}s ago`;
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.floor(h / 24);
  return `${d}d ago`;
}

function fullTimestamp(iso: string): string {
  return new Date(iso).toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  });
}

function LevelBadge({ level }: { level: AppLog['level'] }) {
  const styles: Record<AppLog['level'], { bg: string; color: string; border: string }> = {
    ERROR: {
      bg: 'rgba(255, 93, 93, 0.15)',
      color: 'var(--color-accent-red)',
      border: 'rgba(255, 93, 93, 0.30)',
    },
    WARNING: {
      bg: 'rgba(255, 138, 61, 0.15)',
      color: 'var(--color-accent-orange)',
      border: 'rgba(255, 138, 61, 0.30)',
    },
    INFO: {
      bg: 'rgba(61, 169, 252, 0.12)',
      color: 'var(--color-accent-blue)',
      border: 'rgba(61, 169, 252, 0.25)',
    },
  };
  const s = styles[level];
  return (
    <span
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        fontSize: '10px',
        fontWeight: 700,
        padding: '2px 7px',
        borderRadius: '999px',
        backgroundColor: s.bg,
        color: s.color,
        border: `1px solid ${s.border}`,
        letterSpacing: '0.07em',
        whiteSpace: 'nowrap',
        fontFamily: 'Inter, monospace',
      }}
    >
      {level}
    </span>
  );
}

function StatusCodeBadge({ code }: { code: number }) {
  let color = 'var(--color-text-muted)';
  if (code >= 500) color = 'var(--color-accent-red)';
  else if (code >= 400) color = 'var(--color-accent-orange)';
  else if (code >= 200 && code < 300) color = 'var(--color-accent)';
  return (
    <span style={{ color, fontFamily: 'monospace', fontSize: '12px', fontWeight: 600 }}>
      {code}
    </span>
  );
}

interface LogRowProps {
  log: AppLog;
  isLast: boolean;
  rowIndex: number;
}

function LogRow({ log, isLast, rowIndex }: LogRowProps) {
  const [expanded, setExpanded] = useState(false);
  const isClickable = log.has_traceback || log.message != null;

  return (
    <>
      <tr
        onClick={isClickable ? () => setExpanded((v) => !v) : undefined}
        style={{
          borderBottom: !expanded && !isLast ? '1px solid var(--color-border-subtle)' : 'none',
          backgroundColor: rowIndex % 2 === 0 ? 'transparent' : 'rgba(255,255,255,0.015)',
          cursor: isClickable ? 'pointer' : 'default',
          transition: 'background-color 100ms ease',
        }}
        onMouseEnter={(e) => {
          if (isClickable)
            (e.currentTarget as HTMLTableRowElement).style.backgroundColor = 'rgba(255,255,255,0.04)';
        }}
        onMouseLeave={(e) => {
          (e.currentTarget as HTMLTableRowElement).style.backgroundColor =
            rowIndex % 2 === 0 ? 'transparent' : 'rgba(255,255,255,0.015)';
        }}
      >
        {/* Expand indicator */}
        <td style={{ padding: '10px 8px 10px 14px', width: '20px' }}>
          {isClickable ? (
            expanded ? (
              <ChevronDownIcon style={{ width: '12px', height: '12px', color: 'var(--color-text-muted)' }} />
            ) : (
              <ChevronRightIcon style={{ width: '12px', height: '12px', color: 'var(--color-text-muted)' }} />
            )
          ) : null}
        </td>

        {/* Time */}
        <td
          style={{ padding: '10px 12px', whiteSpace: 'nowrap', color: 'var(--color-text-muted)', fontSize: '12px', fontFamily: 'monospace' }}
          title={fullTimestamp(log.timestamp)}
        >
          {relativeTime(log.timestamp)}
        </td>

        {/* Level */}
        <td style={{ padding: '10px 12px' }}>
          <LevelBadge level={log.level} />
        </td>

        {/* Event type */}
        <td style={{ padding: '10px 12px', color: 'var(--color-text-secondary)', fontSize: '12px', whiteSpace: 'nowrap' }}>
          {log.event_type}
        </td>

        {/* User */}
        <td style={{ padding: '10px 12px', color: 'var(--color-text-muted)', fontSize: '12px', maxWidth: '160px' }}>
          <span
            style={{
              display: 'block',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
            title={log.user_email ?? undefined}
          >
            {log.user_email ?? '—'}
          </span>
        </td>

        {/* Method + Path */}
        <td style={{ padding: '10px 12px', maxWidth: '220px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '6px', overflow: 'hidden' }}>
            {log.method && (
              <span
                style={{
                  fontSize: '10px',
                  fontWeight: 700,
                  fontFamily: 'monospace',
                  color: 'var(--color-accent-blue)',
                  flexShrink: 0,
                }}
              >
                {log.method}
              </span>
            )}
            {log.path && (
              <span
                style={{
                  fontSize: '12px',
                  color: 'var(--color-text-muted)',
                  fontFamily: 'monospace',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
                title={log.path}
              >
                {log.path}
              </span>
            )}
            {!log.method && !log.path && (
              <span style={{ color: 'var(--color-text-muted)', fontSize: '12px' }}>—</span>
            )}
          </div>
        </td>

        {/* Status */}
        <td style={{ padding: '10px 12px', whiteSpace: 'nowrap' }}>
          {log.status_code != null ? <StatusCodeBadge code={log.status_code} /> : <span style={{ color: 'var(--color-text-muted)', fontSize: '12px' }}>—</span>}
        </td>

        {/* Duration */}
        <td style={{ padding: '10px 12px', whiteSpace: 'nowrap', color: 'var(--color-text-muted)', fontSize: '12px', fontFamily: 'monospace' }}>
          {log.duration_ms != null ? `${log.duration_ms}ms` : '—'}
        </td>

        {/* Message */}
        <td style={{ padding: '10px 16px 10px 12px', maxWidth: '280px' }}>
          <span
            style={{
              display: 'block',
              fontSize: '12px',
              color: log.level === 'ERROR' ? 'var(--color-accent-red)' : 'var(--color-text-secondary)',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              opacity: log.message ? 1 : 0.4,
            }}
            title={log.message ?? undefined}
          >
            {log.message ?? 'No message'}
          </span>
        </td>
      </tr>

      {/* Expanded traceback / detail row */}
      {expanded && (
        <tr style={{ borderBottom: !isLast ? '1px solid var(--color-border-subtle)' : 'none' }}>
          <td colSpan={9} style={{ padding: '0 14px 14px 34px' }}>
            <div
              style={{
                backgroundColor: '#0d1117',
                border: '1px solid var(--color-border-subtle)',
                borderRadius: '10px',
                padding: '14px 16px',
                overflow: 'auto',
                maxHeight: '320px',
                animation: 'admin-slide-up 0.15s ease both',
              }}
            >
              {log.has_traceback && log.traceback ? (
                <pre
                  style={{
                    margin: 0,
                    fontFamily: '"JetBrains Mono", "Fira Code", Consolas, monospace',
                    fontSize: '11px',
                    lineHeight: '1.6',
                    color: '#e06c75',
                    whiteSpace: 'pre-wrap',
                    wordBreak: 'break-word',
                  }}
                >
                  {log.traceback}
                </pre>
              ) : log.message ? (
                <p
                  style={{
                    margin: 0,
                    fontFamily: 'monospace',
                    fontSize: '12px',
                    color: 'var(--color-text-secondary)',
                    lineHeight: '1.6',
                  }}
                >
                  {log.message}
                </p>
              ) : (
                <p style={{ margin: 0, fontSize: '12px', color: 'var(--color-text-muted)' }}>
                  No additional details.
                </p>
              )}
              {log.extra && Object.keys(log.extra).length > 0 && (
                <details style={{ marginTop: '10px' }}>
                  <summary
                    style={{
                      fontSize: '11px',
                      color: 'var(--color-text-muted)',
                      cursor: 'pointer',
                      userSelect: 'none',
                      marginBottom: '6px',
                    }}
                  >
                    Extra fields
                  </summary>
                  <pre
                    style={{
                      margin: 0,
                      fontFamily: 'monospace',
                      fontSize: '11px',
                      color: 'var(--color-text-secondary)',
                      lineHeight: '1.6',
                    }}
                  >
                    {JSON.stringify(log.extra, null, 2)}
                  </pre>
                </details>
              )}
            </div>
          </td>
        </tr>
      )}
    </>
  );
}

interface LogsTabProps {
  onToast: (type: 'success' | 'error', message: string) => void;
}

function LogsTab({ onToast }: LogsTabProps) {
  const pageSize = 50;
  const [logs, setLogs] = useState<AppLog[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [autoRefresh, setAutoRefresh] = useState(false);

  // Filters
  const [levelFilter, setLevelFilter] = useState<LogLevel>('ALL');
  const [eventType, setEventType] = useState<LogEventType>('all');
  const [timeRange, setTimeRange] = useState<TimeRange>('24h');
  const [search, setSearch] = useState('');
  const [searchInput, setSearchInput] = useState('');

  const fetchLogs = useCallback(async (silent = false) => {
    if (!silent) setLoading(true);
    const params: LogQueryParams = {
      page,
      page_size: pageSize,
      since: timeRangeToSince(timeRange),
    };
    if (levelFilter !== 'ALL') params.level = levelFilter;
    if (eventType !== 'all') params.event_type = eventType;
    if (search.trim()) params.search = search.trim();
    try {
      const res: LogListResponse = await getLogs(params);
      setLogs(res.logs);
      setTotal(res.total);
    } catch {
      onToast('error', 'Failed to load logs.');
    } finally {
      if (!silent) setLoading(false);
    }
  }, [page, levelFilter, eventType, timeRange, search, onToast]);

  // Fetch when filters change
  useEffect(() => {
    fetchLogs();
  }, [fetchLogs]);

  // Reset to page 1 when filters change (not page itself)
  useEffect(() => {
    setPage(1);
  }, [levelFilter, eventType, timeRange, search]);

  // Auto-refresh every 30s
  useEffect(() => {
    if (!autoRefresh) return;
    const id = setInterval(() => fetchLogs(true), 30_000);
    return () => clearInterval(id);
  }, [autoRefresh, fetchLogs]);

  function handleSearchKeyDown(e: React.KeyboardEvent<HTMLInputElement>) {
    if (e.key === 'Enter') setSearch(searchInput);
  }

  const totalPages = Math.ceil(total / pageSize);

  const levelOptions: { id: LogLevel; label: string; color: string }[] = [
    { id: 'ALL', label: 'All', color: 'var(--color-text-secondary)' },
    { id: 'ERROR', label: 'ERROR', color: 'var(--color-accent-red)' },
    { id: 'WARNING', label: 'WARN', color: 'var(--color-accent-orange)' },
    { id: 'INFO', label: 'INFO', color: 'var(--color-accent-blue)' },
  ];

  const eventOptions: { id: LogEventType; label: string }[] = [
    { id: 'all', label: 'All events' },
    { id: 'error', label: 'error' },
    { id: 'request', label: 'request' },
    { id: 'auth', label: 'auth' },
    { id: 'evaluation', label: 'evaluation' },
  ];

  const timeOptions: { id: TimeRange; label: string }[] = [
    { id: '1h', label: '1h' },
    { id: '6h', label: '6h' },
    { id: '24h', label: '24h' },
    { id: '7d', label: '7d' },
  ];

  return (
    <>
      {/* Filter bar */}
      <div
        style={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: '10px',
          alignItems: 'center',
          marginBottom: '16px',
          padding: '14px 16px',
          backgroundColor: 'var(--color-dark-elevated-1)',
          border: '1px solid var(--color-border-subtle)',
          borderRadius: '14px',
        }}
      >
        {/* Level pills */}
        <div style={{ display: 'flex', gap: '5px', alignItems: 'center' }}>
          {levelOptions.map((opt) => {
            const active = levelFilter === opt.id;
            return (
              <button
                key={opt.id}
                type="button"
                onClick={() => setLevelFilter(opt.id)}
                style={{
                  height: '30px',
                  padding: '0 11px',
                  borderRadius: '999px',
                  border: `1px solid ${active ? opt.color + '55' : 'var(--color-border-subtle)'}`,
                  backgroundColor: active ? opt.color + '18' : 'transparent',
                  color: active ? opt.color : 'var(--color-text-muted)',
                  fontSize: '11px',
                  fontWeight: 700,
                  fontFamily: 'monospace',
                  letterSpacing: '0.05em',
                  cursor: 'pointer',
                  transition: 'all 130ms ease',
                }}
              >
                {opt.label}
              </button>
            );
          })}
        </div>

        <div
          style={{
            width: '1px',
            height: '20px',
            backgroundColor: 'var(--color-border-subtle)',
            flexShrink: 0,
          }}
        />

        {/* Event type pills */}
        <div style={{ display: 'flex', gap: '5px', alignItems: 'center' }}>
          {eventOptions.map((opt) => {
            const active = eventType === opt.id;
            return (
              <button
                key={opt.id}
                type="button"
                onClick={() => setEventType(opt.id)}
                style={{
                  height: '30px',
                  padding: '0 11px',
                  borderRadius: '999px',
                  border: `1px solid ${active ? 'var(--color-accent)' : 'var(--color-border-subtle)'}`,
                  backgroundColor: active ? 'rgba(30,215,96,0.10)' : 'transparent',
                  color: active ? 'var(--color-accent)' : 'var(--color-text-muted)',
                  fontSize: '12px',
                  fontWeight: 500,
                  cursor: 'pointer',
                  transition: 'all 130ms ease',
                }}
              >
                {opt.label}
              </button>
            );
          })}
        </div>

        <div
          style={{
            width: '1px',
            height: '20px',
            backgroundColor: 'var(--color-border-subtle)',
            flexShrink: 0,
          }}
        />

        {/* Time range pills */}
        <div style={{ display: 'flex', gap: '5px', alignItems: 'center' }}>
          {timeOptions.map((opt) => {
            const active = timeRange === opt.id;
            return (
              <button
                key={opt.id}
                type="button"
                onClick={() => setTimeRange(opt.id)}
                style={{
                  height: '30px',
                  padding: '0 11px',
                  borderRadius: '999px',
                  border: `1px solid ${active ? 'var(--color-accent-gold)' : 'var(--color-border-subtle)'}`,
                  backgroundColor: active ? 'rgba(244,201,93,0.10)' : 'transparent',
                  color: active ? 'var(--color-accent-gold)' : 'var(--color-text-muted)',
                  fontSize: '12px',
                  fontWeight: 500,
                  cursor: 'pointer',
                  transition: 'all 130ms ease',
                }}
              >
                {opt.label}
              </button>
            );
          })}
        </div>

        {/* Search */}
        <div style={{ position: 'relative', flex: '1', minWidth: '160px', maxWidth: '300px' }}>
          <MagnifyingGlassIcon
            style={{
              position: 'absolute',
              left: '9px',
              top: '50%',
              transform: 'translateY(-50%)',
              width: '14px',
              height: '14px',
              color: 'var(--color-text-muted)',
              pointerEvents: 'none',
            }}
          />
          <input
            type="text"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            onKeyDown={handleSearchKeyDown}
            placeholder="Search message or path… (Enter)"
            style={{
              width: '100%',
              height: '30px',
              borderRadius: '999px',
              border: '1px solid var(--color-border-subtle)',
              backgroundColor: 'var(--color-dark-elevated-2)',
              color: 'var(--color-text-primary)',
              fontSize: '12px',
              padding: '0 12px 0 30px',
              outline: 'none',
              transition: 'border-color 150ms ease',
              fontFamily: 'Inter, sans-serif',
            }}
            onFocus={(e) => { e.currentTarget.style.borderColor = 'var(--color-accent)'; }}
            onBlur={(e) => { e.currentTarget.style.borderColor = 'var(--color-border-subtle)'; }}
          />
        </div>

        {/* Right-side controls */}
        <div style={{ marginLeft: 'auto', display: 'flex', gap: '8px', alignItems: 'center' }}>
          {/* Auto-refresh toggle */}
          <button
            type="button"
            onClick={() => setAutoRefresh((v) => !v)}
            title={autoRefresh ? 'Auto-refresh ON (every 30s)' : 'Auto-refresh OFF'}
            style={{
              height: '30px',
              padding: '0 11px',
              borderRadius: '999px',
              border: `1px solid ${autoRefresh ? 'rgba(30,215,96,0.40)' : 'var(--color-border-subtle)'}`,
              backgroundColor: autoRefresh ? 'rgba(30,215,96,0.10)' : 'transparent',
              color: autoRefresh ? 'var(--color-accent)' : 'var(--color-text-muted)',
              fontSize: '11px',
              fontWeight: 500,
              cursor: 'pointer',
              transition: 'all 130ms ease',
              display: 'flex',
              alignItems: 'center',
              gap: '5px',
            }}
          >
            <span
              style={{
                width: '6px',
                height: '6px',
                borderRadius: '999px',
                backgroundColor: autoRefresh ? 'var(--color-accent)' : 'var(--color-text-muted)',
                flexShrink: 0,
              }}
            />
            30s
          </button>

          {/* Refresh button */}
          <button
            type="button"
            onClick={() => fetchLogs()}
            title="Refresh logs"
            style={{
              width: '30px',
              height: '30px',
              borderRadius: '999px',
              border: '1px solid var(--color-border-subtle)',
              backgroundColor: 'transparent',
              color: 'var(--color-text-muted)',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              transition: 'all 130ms ease',
            }}
            onMouseEnter={(e) => {
              (e.currentTarget as HTMLButtonElement).style.color = 'var(--color-text-primary)';
              (e.currentTarget as HTMLButtonElement).style.borderColor = 'var(--color-border-strong)';
            }}
            onMouseLeave={(e) => {
              (e.currentTarget as HTMLButtonElement).style.color = 'var(--color-text-muted)';
              (e.currentTarget as HTMLButtonElement).style.borderColor = 'var(--color-border-subtle)';
            }}
          >
            <ArrowPathIcon style={{ width: '13px', height: '13px' }} />
          </button>
        </div>
      </div>

      {/* Total count */}
      <div style={{ marginBottom: '8px', fontSize: '12px', color: 'var(--color-text-muted)' }}>
        {loading ? 'Loading…' : `${total.toLocaleString()} log${total !== 1 ? 's' : ''} found`}
      </div>

      {/* Table */}
      <div
        style={{
          backgroundColor: 'var(--color-dark-card)',
          border: '1px solid var(--color-border-subtle)',
          borderRadius: '16px',
          overflow: 'hidden',
        }}
      >
        {loading ? (
          <div style={{ display: 'flex', justifyContent: 'center', padding: '48px' }}>
            <Spinner />
          </div>
        ) : logs.length === 0 ? (
          <div
            style={{
              textAlign: 'center',
              padding: '64px 24px',
              color: 'var(--color-text-muted)',
            }}
          >
            <DocumentTextIcon
              style={{ width: '32px', height: '32px', marginBottom: '12px', opacity: 0.35 }}
            />
            <p style={{ margin: 0, fontSize: '14px', fontWeight: 500 }}>No logs found</p>
            <p style={{ margin: '6px 0 0', fontSize: '12px', opacity: 0.7 }}>
              Try adjusting the level, event type, or time range filters.
            </p>
          </div>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px', minWidth: '900px' }}>
              <thead>
                <tr style={{ borderBottom: '1px solid var(--color-border-subtle)' }}>
                  <th style={{ width: '20px', padding: '10px 8px 10px 14px' }} />
                  {['Time', 'Level', 'Event', 'User', 'Path', 'Status', 'Duration', 'Message'].map(
                    (col) => (
                      <th
                        key={col}
                        style={{
                          padding: '10px 12px',
                          textAlign: 'left',
                          fontSize: '10px',
                          fontWeight: 600,
                          color: 'var(--color-text-muted)',
                          letterSpacing: '0.07em',
                          textTransform: 'uppercase',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {col}
                      </th>
                    )
                  )}
                </tr>
              </thead>
              <tbody>
                {logs.map((log, idx) => (
                  <LogRow
                    key={log.id}
                    log={log}
                    isLast={idx === logs.length - 1}
                    rowIndex={idx}
                  />
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginTop: '16px',
          }}
        >
          <span style={{ fontSize: '12px', color: 'var(--color-text-muted)' }}>
            {total.toLocaleString()} logs · page {page} of {totalPages}
          </span>
          <div style={{ display: 'flex', gap: '8px' }}>
            <Button
              variant="outline"
              size="sm"
              disabled={page <= 1}
              onClick={() => setPage((p) => p - 1)}
            >
              Previous
            </Button>
            <Button
              variant="outline"
              size="sm"
              disabled={page >= totalPages}
              onClick={() => setPage((p) => p + 1)}
            >
              Next
            </Button>
          </div>
        </div>
      )}
    </>
  );
}

// ─── Registration Requests Tab ────────────────────────────────────────────────

type ReqStatusFilter = 'pending' | 'all';

interface RegistrationRequestsTabProps {
  onToast: (type: 'success' | 'error', message: string) => void;
  onPendingCountChange?: (count: number) => void;
}

function RequestStatusBadge({ status }: { status: RegistrationRequest['status'] }) {
  const map: Record<string, { color: string; bg: string; border: string }> = {
    pending: {
      color: 'var(--color-accent-gold)',
      bg: 'rgba(244,201,93,0.12)',
      border: 'rgba(244,201,93,0.30)',
    },
    approved: {
      color: 'var(--color-accent)',
      bg: 'rgba(30,215,96,0.12)',
      border: 'rgba(30,215,96,0.30)',
    },
    declined: {
      color: 'var(--color-text-muted)',
      bg: 'rgba(120,120,120,0.10)',
      border: 'rgba(120,120,120,0.25)',
    },
    blocked: {
      color: 'var(--color-accent-red)',
      bg: 'rgba(255,93,93,0.12)',
      border: 'rgba(255,93,93,0.30)',
    },
  };
  const s = map[status] ?? map.declined;
  return (
    <span
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        fontSize: '11px',
        fontWeight: 600,
        padding: '2px 8px',
        borderRadius: '999px',
        backgroundColor: s.bg,
        color: s.color,
        border: `1px solid ${s.border}`,
        textTransform: 'capitalize',
        whiteSpace: 'nowrap',
      }}
    >
      {status}
    </span>
  );
}

function RegistrationRequestsTab({ onToast, onPendingCountChange }: RegistrationRequestsTabProps) {
  const [requests, setRequests] = useState<RegistrationRequest[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [statusFilter, setStatusFilter] = useState<ReqStatusFilter>('pending');
  const [pendingCount, setPendingCount] = useState<number | null>(null);
  const [actingId, setActingId] = useState<string | null>(null);

  const fetch = useCallback(async () => {
    setLoading(true);
    try {
      const [res, pendingRes] = await Promise.all([
        listRegistrationRequests({
          status: statusFilter === 'pending' ? 'pending' : undefined,
          page: 1,
          page_size: 50,
        }),
        // Always fetch pending count for badge
        statusFilter !== 'pending'
          ? listRegistrationRequests({ status: 'pending', page: 1, page_size: 1 })
          : null,
      ]);
      setRequests(res.items);
      setTotal(res.total);
      const pc = statusFilter === 'pending' ? res.total : (pendingRes?.total ?? pendingCount ?? 0);
      setPendingCount(pc);
      onPendingCountChange?.(pc);
    } catch {
      onToast('error', 'Failed to load registration requests.');
    } finally {
      setLoading(false);
    }
  }, [statusFilter, onToast, onPendingCountChange, pendingCount]);

  useEffect(() => {
    fetch();
  }, [fetch]);

  async function handleApprove(id: string) {
    setActingId(id);
    try {
      await approveRegistrationRequest(id);
      onToast('success', 'Request approved — invitation sent.');
      fetch();
    } catch {
      onToast('error', 'Failed to approve request.');
    } finally {
      setActingId(null);
    }
  }

  async function handleDecline(id: string) {
    setActingId(id);
    try {
      await declineRegistrationRequest(id, { notify_requester: true });
      onToast('success', 'Request declined.');
      fetch();
    } catch {
      onToast('error', 'Failed to decline request.');
    } finally {
      setActingId(null);
    }
  }

  async function handleBlock(id: string) {
    setActingId(id);
    try {
      await blockRegistrationRequest(id);
      onToast('success', 'Email blocked.');
      fetch();
    } catch {
      onToast('error', 'Failed to block request.');
    } finally {
      setActingId(null);
    }
  }

  const filterOptions: { id: ReqStatusFilter; label: string }[] = [
    { id: 'pending', label: 'Pending' },
    { id: 'all', label: 'All' },
  ];

  return (
    <>
      {/* Filter chips + count */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          marginBottom: '16px',
        }}
      >
        {filterOptions.map((opt) => {
          const active = statusFilter === opt.id;
          return (
            <button
              key={opt.id}
              type="button"
              onClick={() => setStatusFilter(opt.id)}
              style={{
                height: '30px',
                padding: '0 12px',
                borderRadius: '999px',
                border: `1px solid ${active ? 'var(--color-accent-blue)' : 'var(--color-border-subtle)'}`,
                backgroundColor: active ? 'rgba(61,169,252,0.12)' : 'transparent',
                color: active ? 'var(--color-accent-blue)' : 'var(--color-text-muted)',
                fontSize: '12px',
                fontWeight: active ? 600 : 500,
                cursor: 'pointer',
                transition: 'all 130ms ease',
                display: 'flex',
                alignItems: 'center',
                gap: '5px',
              }}
            >
              {opt.label}
              {opt.id === 'pending' && pendingCount != null && pendingCount > 0 && (
                <span
                  style={{
                    minWidth: '18px',
                    height: '18px',
                    borderRadius: '999px',
                    backgroundColor: 'var(--color-accent-blue)',
                    color: 'var(--color-text-inverse)',
                    fontSize: '10px',
                    fontWeight: 700,
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    padding: '0 4px',
                  }}
                >
                  {pendingCount}
                </span>
              )}
            </button>
          );
        })}
        {!loading && (
          <span style={{ fontSize: '12px', color: 'var(--color-text-muted)', marginLeft: 'auto' }}>
            {total} result{total !== 1 ? 's' : ''}
          </span>
        )}
      </div>

      {/* Table */}
      <div
        style={{
          backgroundColor: 'var(--color-dark-elevated-1)',
          border: '1px solid var(--color-border-subtle)',
          borderRadius: '14px',
          overflow: 'hidden',
        }}
      >
        {loading ? (
          <div style={{ display: 'flex', justifyContent: 'center', padding: '48px 0' }}>
            <Spinner />
          </div>
        ) : requests.length === 0 ? (
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              padding: '56px 24px',
              gap: '10px',
            }}
          >
            <ClipboardDocumentListIcon
              style={{ width: '32px', height: '32px', color: 'var(--color-text-muted)' }}
            />
            <p style={{ margin: 0, fontSize: '14px', color: 'var(--color-text-muted)' }}>
              No pending registration requests
            </p>
          </div>
        ) : (
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
            <thead>
              <tr style={{ borderBottom: '1px solid var(--color-border-subtle)' }}>
                {['Email', 'Name', 'Role', 'Submitted', 'Status', 'Actions'].map((h) => (
                  <th
                    key={h}
                    style={{
                      padding: '10px 14px',
                      textAlign: 'left',
                      fontSize: '11px',
                      fontWeight: 600,
                      color: 'var(--color-text-muted)',
                      letterSpacing: '0.05em',
                      textTransform: 'uppercase',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {requests.map((req, idx) => {
                const isPending = req.status === 'pending';
                const isActing = actingId === req.id;
                return (
                  <tr
                    key={req.id}
                    style={{
                      borderBottom:
                        idx < requests.length - 1
                          ? '1px solid var(--color-border-subtle)'
                          : 'none',
                      backgroundColor:
                        idx % 2 === 0 ? 'transparent' : 'rgba(255,255,255,0.015)',
                    }}
                  >
                    <td
                      style={{
                        padding: '10px 14px',
                        color: 'var(--color-text-primary)',
                        maxWidth: '180px',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }}
                      title={req.email}
                    >
                      {req.email}
                    </td>
                    <td
                      style={{
                        padding: '10px 14px',
                        color: 'var(--color-text-secondary)',
                        maxWidth: '140px',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {req.display_name}
                    </td>
                    <td style={{ padding: '10px 14px' }}>
                      <RoleBadge role={req.requested_role} />
                    </td>
                    <td
                      style={{
                        padding: '10px 14px',
                        color: 'var(--color-text-muted)',
                        whiteSpace: 'nowrap',
                        fontSize: '12px',
                      }}
                    >
                      {formatDate(req.created_at)}
                    </td>
                    <td style={{ padding: '10px 14px' }}>
                      <RequestStatusBadge status={req.status} />
                    </td>
                    <td style={{ padding: '10px 14px' }}>
                      {isPending ? (
                        <div style={{ display: 'flex', gap: '6px', alignItems: 'center' }}>
                          <button
                            type="button"
                            disabled={isActing}
                            onClick={() => handleApprove(req.id)}
                            style={{
                              height: '26px',
                              padding: '0 10px',
                              borderRadius: '6px',
                              border: '1px solid rgba(30,215,96,0.30)',
                              backgroundColor: 'rgba(30,215,96,0.08)',
                              color: 'var(--color-accent)',
                              fontSize: '11px',
                              fontWeight: 600,
                              cursor: isActing ? 'not-allowed' : 'pointer',
                              opacity: isActing ? 0.5 : 1,
                              transition: 'all 120ms ease',
                              whiteSpace: 'nowrap',
                            }}
                          >
                            Approve
                          </button>
                          <button
                            type="button"
                            disabled={isActing}
                            onClick={() => handleDecline(req.id)}
                            style={{
                              height: '26px',
                              padding: '0 10px',
                              borderRadius: '6px',
                              border: '1px solid var(--color-border-subtle)',
                              backgroundColor: 'transparent',
                              color: 'var(--color-text-muted)',
                              fontSize: '11px',
                              fontWeight: 500,
                              cursor: isActing ? 'not-allowed' : 'pointer',
                              opacity: isActing ? 0.5 : 1,
                              transition: 'all 120ms ease',
                              whiteSpace: 'nowrap',
                            }}
                          >
                            Decline
                          </button>
                          <button
                            type="button"
                            disabled={isActing}
                            onClick={() => handleBlock(req.id)}
                            style={{
                              height: '26px',
                              padding: '0 10px',
                              borderRadius: '6px',
                              border: '1px solid rgba(255,93,93,0.25)',
                              backgroundColor: 'rgba(255,93,93,0.06)',
                              color: 'var(--color-accent-red)',
                              fontSize: '11px',
                              fontWeight: 500,
                              cursor: isActing ? 'not-allowed' : 'pointer',
                              opacity: isActing ? 0.5 : 1,
                              transition: 'all 120ms ease',
                              whiteSpace: 'nowrap',
                            }}
                          >
                            Block
                          </button>
                        </div>
                      ) : (
                        <span style={{ fontSize: '12px', color: 'var(--color-text-muted)' }}>
                          {req.resolved_at ? formatDate(req.resolved_at) : '—'}
                        </span>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
    </>
  );
}

// ─── AdminPage ────────────────────────────────────────────────────────────────

export default function AdminPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<TabId>('overview');
  const [stats, setStats] = useState<PlatformStats | null>(null);
  const [statsLoading, setStatsLoading] = useState(false);
  const [toast, setToast] = useState<{ type: 'success' | 'error'; message: string } | null>(null);
  const [pendingRequestCount, setPendingRequestCount] = useState<number | null>(null);

  // Guard: only admins can access this page
  useEffect(() => {
    if (user && user.role !== 'admin') {
      navigate('/', { replace: true });
    }
  }, [user, navigate]);

  // Load stats when on overview tab
  useEffect(() => {
    if (activeTab !== 'overview') return;
    let cancelled = false;
    setStatsLoading(true);
    getStats()
      .then((s) => { if (!cancelled) setStats(s); })
      .catch(() => setToast({ type: 'error', message: 'Failed to load platform stats.' }))
      .finally(() => { if (!cancelled) setStatsLoading(false); });
    return () => { cancelled = true; };
  }, [activeTab]);

  const showToast = useCallback((type: 'success' | 'error', message: string) => {
    setToast({ type, message });
  }, []);

  if (!user || user.role !== 'admin') {
    return null;
  }

  return (
    <>
      <style>{`
        @keyframes admin-fade-in {
          from { opacity: 0; }
          to   { opacity: 1; }
        }
        @keyframes admin-slide-up {
          from { opacity: 0; transform: translateY(12px); }
          to   { opacity: 1; transform: translateY(0); }
        }
        @keyframes spin {
          to { transform: rotate(360deg); }
        }
        @keyframes toast-in {
          from { opacity: 0; transform: translateY(12px); }
          to   { opacity: 1; transform: translateY(0); }
        }
      `}</style>

      <div
        style={{
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          padding: '28px 32px',
          minHeight: 0,
          animation: 'admin-fade-in 0.25s ease both',
        }}
      >
        {/* Page header */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '28px' }}>
          <div
            style={{
              width: '42px', height: '42px', borderRadius: '12px',
              backgroundColor: 'rgba(244,201,93,0.12)',
              border: '1px solid rgba(244,201,93,0.25)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              flexShrink: 0,
            }}
          >
            <ShieldCheckIcon style={{ width: '22px', height: '22px', color: 'var(--color-accent-gold)' }} />
          </div>
          <div>
            <h1
              style={{
                fontFamily: 'var(--font-body)',
                fontWeight: 700,
                fontSize: '22px',
                color: 'var(--color-text-primary)',
                margin: 0,
                lineHeight: 1.2,
              }}
            >
              Admin Panel
            </h1>
            <p style={{ margin: '3px 0 0', fontSize: '13px', color: 'var(--color-text-muted)' }}>
              Platform-wide management and oversight
            </p>
          </div>
          <div style={{ marginLeft: 'auto' }}>
            <button
              type="button"
              onClick={() => {
                if (activeTab === 'overview') {
                  setStats(null);
                  setStatsLoading(true);
                  getStats()
                    .then(setStats)
                    .catch(() => showToast('error', 'Failed to refresh stats.'))
                    .finally(() => setStatsLoading(false));
                }
              }}
              style={{
                display: 'flex', alignItems: 'center', gap: '6px',
                height: '36px', padding: '0 14px',
                borderRadius: '999px',
                border: '1px solid var(--color-border-subtle)',
                backgroundColor: 'var(--color-dark-elevated-1)',
                color: 'var(--color-text-secondary)',
                fontSize: '13px', fontWeight: 500,
                cursor: 'pointer',
                transition: 'all 150ms ease',
              }}
              onMouseEnter={(e) => {
                (e.currentTarget as HTMLButtonElement).style.borderColor = 'var(--color-border-strong)';
                (e.currentTarget as HTMLButtonElement).style.color = 'var(--color-text-primary)';
              }}
              onMouseLeave={(e) => {
                (e.currentTarget as HTMLButtonElement).style.borderColor = 'var(--color-border-subtle)';
                (e.currentTarget as HTMLButtonElement).style.color = 'var(--color-text-secondary)';
              }}
            >
              <ArrowPathIcon style={{ width: '14px', height: '14px' }} />
              Refresh
            </button>
          </div>
        </div>

        {/* Tab bar */}
        <div
          style={{
            display: 'flex',
            borderBottom: '1px solid var(--color-border-subtle)',
            marginBottom: '24px',
            gap: '4px',
          }}
        >
          {TABS.map((tab) => {
            const isActive = activeTab === tab.id;
            const Icon = tab.icon;
            return (
              <button
                key={tab.id}
                type="button"
                onClick={() => setActiveTab(tab.id)}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '7px',
                  padding: '10px 16px',
                  background: 'none',
                  border: 'none',
                  borderBottom: `2px solid ${isActive ? 'var(--color-accent)' : 'transparent'}`,
                  color: isActive ? 'var(--color-text-primary)' : 'var(--color-text-muted)',
                  fontSize: '13px',
                  fontWeight: isActive ? 600 : 500,
                  cursor: 'pointer',
                  transition: 'color 150ms ease, border-color 150ms ease',
                  marginBottom: '-1px',
                  whiteSpace: 'nowrap',
                  fontFamily: 'Inter, sans-serif',
                }}
                onMouseEnter={(e) => {
                  if (!isActive) {
                    (e.currentTarget as HTMLButtonElement).style.color = 'var(--color-text-secondary)';
                  }
                }}
                onMouseLeave={(e) => {
                  if (!isActive) {
                    (e.currentTarget as HTMLButtonElement).style.color = 'var(--color-text-muted)';
                  }
                }}
              >
                <Icon style={{ width: '15px', height: '15px', flexShrink: 0 }} aria-hidden="true" />
                {tab.label}
                {/* Pending-count badge on the Requests tab */}
                {tab.id === 'registration_requests' &&
                  pendingRequestCount != null &&
                  pendingRequestCount > 0 && (
                    <span
                      style={{
                        minWidth: '18px',
                        height: '18px',
                        borderRadius: '999px',
                        backgroundColor: 'var(--color-accent-blue)',
                        color: 'var(--color-text-inverse)',
                        fontSize: '10px',
                        fontWeight: 700,
                        display: 'inline-flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        padding: '0 4px',
                        marginLeft: '2px',
                      }}
                    >
                      {pendingRequestCount}
                    </span>
                  )}
              </button>
            );
          })}
        </div>

        {/* Tab content */}
        <div style={{ flex: 1, minHeight: 0, animation: 'admin-fade-in 0.18s ease both' }} key={activeTab}>
          {activeTab === 'overview' && (
            <OverviewTab stats={stats} loading={statsLoading} />
          )}
          {activeTab === 'users' && (
            <UsersTab currentUserId={user.id} onToast={showToast} />
          )}
          {activeTab === 'templates' && (
            <TemplatesTab onToast={showToast} />
          )}
          {activeTab === 'evaluations' && (
            <EvaluationsTab onToast={showToast} />
          )}
          {activeTab === 'logs' && (
            <LogsTab onToast={showToast} />
          )}
          {activeTab === 'registration_requests' && (
            <RegistrationRequestsTab
              onToast={showToast}
              onPendingCountChange={setPendingRequestCount}
            />
          )}
        </div>
      </div>

      {/* Toast notification */}
      {toast && (
        <Toast
          type={toast.type}
          message={toast.message}
          onClose={() => setToast(null)}
        />
      )}
    </>
  );
}
